import { ApiError, isApiError } from './errors';
import type { ApiClient } from './client';
import type { AskAccepted, AskAsyncInput, Citation, UUID } from './types';

/** Cadence of the GET /v1/knowledge/asks/{id} polls. */
export const ASK_POLL_INTERVAL_MS = 2_000;

/** Safety net: a run takes 1–3 minutes; past this, give up as a friendly failure. */
export const ASK_POLL_TIMEOUT_MS = 5 * 60 * 1000;

/** Polls the SAME stage may repeat before the generic patience line takes over. */
export const ASK_STAGE_STALE_POLLS = 3;

/** Marlena's voice for a run with no (fresh) stage to show. */
export const ASK_PROGRESS_FALLBACK =
  'Ainda estou processando sua resposta, obrigada pela paciência…';

export const ASK_TIMEOUT_MESSAGE =
  'A resposta está demorando mais do que o esperado. Tente novamente em instantes.';

const ASK_FAILED_FALLBACK = 'Não consegui concluir sua pergunta. Tente novamente.';

/** What a finished run hands back — the fields the chat surfaces render. */
export interface AskRunResult {
  answer: string;
  sources: Citation[];
  confidence: number;
  /** Null is tolerated (a `done` without it simply cannot receive feedback). */
  interactionId: UUID | null;
}

export interface AskRunCallbacks {
  /** Friendly progress line to show while the run is `running`. */
  onProgress: (message: string) => void;
  onDone: (result: AskRunResult) => void;
  /** Always an ApiError, so the surfaces render the friendly message as-is. */
  onFailed: (error: ApiError) => void;
  /**
   * The 202 receipt. It is the only answer that names the thread the turn
   * landed in (`conversationId`), so a surface that opened a new thread learns
   * its id here — the polls never repeat it.
   */
  onAccepted?: (accepted: AskAccepted) => void;
}

/**
 * The progress line for a poll: the backend's stage while it is fresh, the
 * generic patience line once the same stage repeated too many times (or none
 * arrived) — a stuck stage must not read as a stuck app.
 */
export function askProgressMessage(stage: string | null, stalePolls: number): string {
  return stage && stalePolls < ASK_STAGE_STALE_POLLS ? stage : ASK_PROGRESS_FALLBACK;
}

/**
 * Poll one already-accepted ask run to its terminal state.
 *
 * Returns a cancel function. Terminal states and the safety timeout stop the
 * polling on their own. A failed poll does NOT fail the run: a network blip
 * or 5xx is retried on the next tick (the run keeps cooking server-side),
 * while a 4xx means the run is gone for good and fails immediately.
 *
 * The timeout clock starts when this poll starts, not when the run started —
 * the server-side stale detector (a run silent for 5 minutes reads as failed)
 * is the real backstop for a run orphaned by a closed tab.
 */
function pollAskJob(
  api: Pick<ApiClient, 'getAsk'>,
  askId: UUID,
  callbacks: AskRunCallbacks,
): () => void {
  let cancelled = false;
  let timer: ReturnType<typeof setTimeout> | null = null;
  const startedAt = Date.now();
  let lastStage: string | null = null;
  let stalePolls = 0;

  const fail = (error: ApiError): void => {
    if (cancelled) return;
    cancelled = true;
    if (timer) clearTimeout(timer);
    callbacks.onFailed(error);
  };

  const schedulePoll = (): void => {
    if (cancelled) return;
    if (Date.now() - startedAt >= ASK_POLL_TIMEOUT_MS) {
      fail(new ApiError(0, { code: 'ask_timeout', message: ASK_TIMEOUT_MESSAGE }));
      return;
    }
    timer = setTimeout(() => {
      void pollNow();
    }, ASK_POLL_INTERVAL_MS);
  };

  const pollNow = async (): Promise<void> => {
    try {
      const job = await api.getAsk(askId);
      if (cancelled) return;
      if (job.status === 'done') {
        cancelled = true;
        callbacks.onDone({
          answer: job.answer ?? '',
          sources: job.sources ?? [],
          confidence: job.confidence ?? 0,
          interactionId: job.interactionId,
        });
        return;
      }
      if (job.status === 'failed') {
        fail(new ApiError(0, { code: 'ask_failed', message: job.error ?? ASK_FAILED_FALLBACK }));
        return;
      }
      if (job.stage === lastStage) stalePolls += 1;
      else {
        lastStage = job.stage;
        stalePolls = 0;
      }
      callbacks.onProgress(askProgressMessage(job.stage, stalePolls));
      schedulePoll();
    } catch (error) {
      if (cancelled) return;
      if (isApiError(error) && error.status >= 400 && error.status < 500) {
        fail(error);
        return;
      }
      // Transient (network/5xx): the run is still cooking — keep polling; the
      // timeout above is the backstop that keeps this from spinning forever.
      schedulePoll();
    }
  };

  schedulePoll();

  return () => {
    cancelled = true;
    if (timer) clearTimeout(timer);
  };
}

/**
 * Run one question through the async ask flow (202 + polling), keeping the
 * contract → UI mapping in exactly one place like ask-stream.ts did for SSE.
 * Used by the Knowledge chat and the Shiba widget.
 *
 * Submits, reports the receipt through `onAccepted` and delegates the polling
 * to {@link pollAskJob}. The returned cancel stops whichever phase is in
 * flight; terminal states stop the polling on their own.
 */
export function runAskAsync(
  api: Pick<ApiClient, 'askAsync' | 'getAsk'>,
  input: AskAsyncInput,
  callbacks: AskRunCallbacks,
): () => void {
  let cancelled = false;
  let cancelPoll: (() => void) | null = null;

  void (async () => {
    try {
      const accepted = await api.askAsync(input);
      if (cancelled) return;
      callbacks.onAccepted?.(accepted);
      callbacks.onProgress(askProgressMessage(accepted.stage, 0));
      cancelPoll = pollAskJob(api, accepted.askId, callbacks);
    } catch (error) {
      if (cancelled) return;
      callbacks.onFailed(
        isApiError(error)
          ? error
          : new ApiError(0, {
              code: 'ask_submit_failed',
              message: 'Não foi possível enviar sua pergunta. Tente novamente.',
            }),
      );
    }
  })();

  return () => {
    cancelled = true;
    cancelPoll?.();
  };
}
